import type {
  LogPanelFilterEdit,
  SyntheticFilterTarget,
} from "@/shared/services/AiAgentThreadPubSubBus/models";
import { SYNTHETIC_FILTER_TARGETS } from "@/shared/services/AiAgentThreadPubSubBus/models";
import { FILTER_CONDITIONS } from "@/shared/state/visualization/schema/v1/log-panel";

/*
 * The log panel filter tool's input contract, shared by the tool's `execute`
 * and its approval card so the card can never preview a call `execute`
 * refuses. Lives apart from the tool module because the tool imports the card.
 */

/** Upper bound on filter edits per tool call. */
export const MAX_FILTER_EDITS_PER_CALL = 10;
/** Upper bound on stages per filter edit in one call; the panel UI has no cap. */
export const MAX_STAGES_PER_EDIT = 5;

const CONDITION_SET = new Set<string>(FILTER_CONDITIONS);
const SYNTHETIC_TARGET_SET = new Set<string>(SYNTHETIC_FILTER_TARGETS);

// ---------------------------------------------------------------------------
// Input types
// ---------------------------------------------------------------------------

export interface LogPanelFilterEditInput {
  /** Message path id of the column to filter. Exclusive with synthetic_target. */
  message_path_id?: string;
  /** A synthetic column: "all_columns" or "log_time". Exclusive with message_path_id. */
  synthetic_target?: SyntheticFilterTarget;
  stages?: {
    condition: string;
    value?: string;
    value2?: string;
  }[];
  combinator?: "or" | "and";
  mode?: "replace" | "append";
}

export interface SetLogPanelFilterInput {
  panel_id: string;
  edits: LogPanelFilterEditInput[];
}

/**
 * Shape-validates one edit and maps it to the workspace-action form. Semantic
 * validation (does the column exist, is the condition valid for its data
 * type, numeric parses, the between/value2 rule) happens in the workspace
 * handler, which sees the panel's data.
 */
export function mapEdit(
  edit: LogPanelFilterEditInput,
  index: number,
): { edit: LogPanelFilterEdit } | { error: string } {
  const problem = (reason: string) => ({ error: `edits[${index}]: ${reason}` });

  // Model-authored input: the schema is guidance, not a guarantee, so a null or
  // scalar entry is possible. Reject it as an input error the model can act on
  // rather than dereferencing it into a TypeError that surfaces as a transport
  // failure — the same guard each stage already gets below.
  if (typeof edit !== "object" || edit === null) {
    return problem("each edit must be an object");
  }

  // Presence decides the target, type is checked after: testing one field by
  // type and the other by presence lets a malformed id fall away silently and
  // retarget the edit onto the synthetic column.
  const hasMessagePath =
    edit.message_path_id !== undefined && edit.message_path_id !== null;
  const hasSynthetic =
    edit.synthetic_target !== undefined && edit.synthetic_target !== null;
  if (hasMessagePath === hasSynthetic) {
    return problem(
      "exactly one of message_path_id or synthetic_target is required",
    );
  }
  if (hasMessagePath && typeof edit.message_path_id !== "string") {
    return problem(
      "exactly one of message_path_id or synthetic_target is required, as a string",
    );
  }
  if (
    hasSynthetic &&
    !SYNTHETIC_TARGET_SET.has(edit.synthetic_target as string)
  ) {
    return problem(
      `synthetic_target must be one of: ${SYNTHETIC_FILTER_TARGETS.join(", ")}`,
    );
  }

  // No default: an absent or null stages list must not become the empty
  // replace that clears a column. Clearing is spelled out as `stages: []`.
  const stages = edit.stages;
  if (stages === undefined || stages === null) {
    return problem(
      "stages is required; pass [] with mode 'replace' to clear the column's filter",
    );
  }
  if (!Array.isArray(stages) || stages.length > MAX_STAGES_PER_EDIT) {
    return problem(`stages must be an array of at most ${MAX_STAGES_PER_EDIT}`);
  }
  const mappedStages: LogPanelFilterEdit["stages"] = [];
  for (const stage of stages) {
    if (typeof stage !== "object" || stage === null) {
      return problem("each stage must be an object");
    }
    if (
      typeof stage.condition !== "string" ||
      !CONDITION_SET.has(stage.condition)
    ) {
      return problem(
        `stage condition must be one of: ${FILTER_CONDITIONS.join(", ")}`,
      );
    }
    if (stage.value !== undefined && typeof stage.value !== "string") {
      return problem("stage value must be a string");
    }
    if (stage.value2 !== undefined && typeof stage.value2 !== "string") {
      return problem("stage value2 must be a string");
    }
    mappedStages.push({
      value: stage.value ?? "",
      condition: stage.condition as (typeof FILTER_CONDITIONS)[number],
      ...(stage.value2 !== undefined && { value2: stage.value2 }),
    });
  }

  // Absent defaults to replace; null is malformed, not absent.
  const mode = edit.mode === undefined ? "replace" : edit.mode;
  if (mode !== "replace" && mode !== "append") {
    return problem('mode must be "replace" or "append"');
  }
  if (mode === "append" && mappedStages.length === 0) {
    return problem(
      "append requires at least one stage; to clear the column's filter use mode 'replace' with stages: []",
    );
  }
  if (
    edit.combinator !== undefined &&
    !["or", "and"].includes(edit.combinator)
  ) {
    return problem('combinator must be "or" or "and"');
  }

  return {
    edit: {
      target: hasSynthetic
        ? (edit.synthetic_target as SyntheticFilterTarget)
        : { messagePathId: edit.message_path_id as string },
      stages: mappedStages,
      ...(edit.combinator !== undefined && { combinator: edit.combinator }),
      mode,
    },
  };
}
